/* eslint-disable @typescript-eslint/no-explicit-any */
import { queryKeyFactory } from '@wyz/utils';
import { CommonQueryKeys } from '~/common/api';
import { IDimensionParameterQuery, ISearchTypeArgs } from '~/purchase/types';

export const cart = queryKeyFactory({
  base: (id: string) => [...CommonQueryKeys.root(), 'cart', id],
  refresh: (idCustomer: string) => ['refresh-cart-items', idCustomer],
});

export const parameters = queryKeyFactory({
  base: (customerId: string) => [
    ...CommonQueryKeys.root(),
    'purchase-parameters',
    customerId,
  ],
  dimension: function (customerId: string, query?: IDimensionParameterQuery) {
    return [...this.base(customerId), 'dimension', query];
  },
  vehicleTypes: function (customerId: string, id: string) {
    return [...this.base(customerId), 'vehicle_types', id];
  },
  seasons: function () {
    return [...this.base('customerId'), 'seasons'];
  },
});

export const search = queryKeyFactory({
  base: (
    customerId: string,
    searchType: ISearchTypeArgs['searchType'],
    query?: Record<string, any>,
  ) => [...CommonQueryKeys.root(), 'products', searchType, customerId, query],
  count: function (
    customerId: string,
    searchType: ISearchTypeArgs['searchType'],
    query?: Record<string, any>,
  ) {
    return [...this.base(customerId, searchType, query), 'count'];
  },
  filtered: function (
    customerId: string,
    searchType: ISearchTypeArgs['searchType'],
    query: Record<string, any>,
    filter: Record<string, any>,
  ) {
    return [...this.base(customerId, searchType, query), filter];
  },
});

export const quotes = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'quotes'],
  paginate: function (customerId: string) {
    return [...this.base(), 'paginate', customerId];
  },
});

export const diagnoses = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'quote-diagnoses'],
  paginate: function (customerCode: string) {
    return [...this.base(), 'paginate', customerCode];
  },
});

export const orders = queryKeyFactory({
  base: (customerId: string) => [
    ...CommonQueryKeys.root(),
    'orders',
    customerId,
  ],
  distinctSuppliers: (
    tenantCode: string,
    customerId: string,
    includesCustomerChildren: boolean,
  ) => ['orders-suppliers', tenantCode, customerId, includesCustomerChildren],
  distinctStatuses: (tenantCode: string, customerId: string) => [
    'orders-statuses',
    tenantCode,
    customerId,
  ],
  detail: function (reference: string) {
    return [...CommonQueryKeys.root(), 'orders', 'detail', reference];
  },
  pending: function (customerId: string) {
    return [...this.base(customerId), 'pending'];
  },
  processed: function (customerId: string) {
    return [...this.base(customerId), 'reviewed'];
  },
  countUnsynchronized: function (customerId: string) {
    return [...this.base(customerId), 'unsynchronized-orders-count'];
  },
  unsynchronized: function (customerId: string) {
    return [...this.base(customerId), 'unsynchronized-orders'];
  },
  generatedOrders: function (reference: string) {
    return [...this.detail(reference), 'generated'];
  },
});
export const searchHistory = queryKeyFactory({
  base: (customerId: string, type: string) => [
    ...CommonQueryKeys.root(),
    'search-history',
    type,
    customerId,
  ],
});

export const wcwSearchHistory = queryKeyFactory({
  base: (customerId: string) => [
    ...CommonQueryKeys.root(),
    'wcw-search-history',
    customerId,
  ],
});

export const quotePricingPolicies = queryKeyFactory({
  base: (customerId: string) => [
    ...CommonQueryKeys.root(),
    'quote-pricing-policies',
    customerId,
  ],
  detail: function (groupId: string) {
    return [...this.base(), 'detail', groupId];
  },
  conflicted: function (query: unknown) {
    return [...this.base(), 'conflicted', query];
  },
  active: function (cusomterId: string) {
    return [...this.base(), cusomterId, 'active'];
  },
});

export const quoteDefaultPricingPolicies = queryKeyFactory({
  base: () => [
    ...CommonQueryKeys.root(),
    'quote-default-service-pricing-policies',
  ],
});

export const redistributionPricingPolicies = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'redistribution-pricing-policies'],
  detail: function (id: string) {
    return [...this.base(), 'detail', id];
  },
  edit: function (id: string) {
    return [...this.base(), 'edit', id];
  },
  remove: function () {
    return [...this.base(), 'remove'];
  },
});
export const quoteServicePricingPolicies = queryKeyFactory({
  base: (customer?: string) =>
    [
      ...CommonQueryKeys.root(),
      'quote-service-pricing-policies',
      customer,
    ].filter(Boolean),
  matching: function (customerId: string, options) {
    return [this.base(customerId), 'matching', options];
  },
  get: function (policyGroupId: string) {
    return [...this.base(), policyGroupId];
  },
  active: function (customerId: string) {
    return [...this.base(), customerId, 'active'];
  },
});
export const redistributionServicePricingPolicies = queryKeyFactory({
  base: (customerId: string) => [
    ...CommonQueryKeys.root(),
    'redistribution-service-pricing-policies',
    customerId,
  ],
  get: function (customerId: string, policyGroupId: string) {
    return [...this.base(customerId), policyGroupId];
  },
  conflicted: function (customerId: string, query: unknown) {
    return [...this.base(customerId), 'conflicted', query];
  },
});

export const quoteCart = queryKeyFactory({
  base: (id: string) => [...CommonQueryKeys.root(), 'quote-cart', id],
  submit: function (idCustomer: string) {
    return [...this.base(idCustomer), 'submit'];
  },
});

export const serviceQuoteCart = queryKeyFactory({
  base: (id: string) => [...CommonQueryKeys.root(), 'service-quote-cart', id],
  submit: function (id: string) {
    return [...this.base(id), 'submit'];
  },
  reset: function (id: string) {
    return [...this.base(id), 'reset'];
  },
});

export const quoteSetting = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'quote-setting'],
  get: function (customerId: string) {
    return [...this.base(), customerId];
  },
  list: function (args) {
    return [...this.base(), 'list', args];
  },
});

export const getFromQuote = queryKeyFactory({
  base: (id: string) => [...CommonQueryKeys.root(), 'get-from-quote', id],
});

export const redistribution = queryKeyFactory({
  base: (idCustomer: string) => [
    ...CommonQueryKeys.root(),
    'redistribution-search',
    idCustomer,
  ],
  suggestedOffers: function (orderReference: string) {
    return [...this.base(), 'suggested-offers', orderReference];
  },
});

export const quoteProximityStocks = queryKeyFactory({
  base: (id: string, identifiers: string[]) => [
    ...CommonQueryKeys.root(),
    'quote-proximity-stocks',
    id,
    identifiers,
  ],
});

export const quoteDefaultServicePricingPolicies = queryKeyFactory({
  base: () => [
    ...CommonQueryKeys.root(),
    'quote-default-service-pricing-policies',
  ],
});

export const wcw = queryKeyFactory({
  base: (customerId: string) => [...CommonQueryKeys.root(), 'wcw', customerId],
  refresh: (customerId: string) => ['refresh-wcw-cart-items', customerId],
  brands: function (customerId: string) {
    return [...this.base(customerId), 'brands'];
  },
  models: function (customerId: string, brand: string) {
    return [...this.base(customerId), 'models', brand];
  },
  versions: function (customerId: string, brand: string, model: string) {
    return [...this.base(customerId), 'versions', brand, model];
  },
  supplierAvailability: function (customerId: string) {
    return [...this.base(customerId), 'supplier-availability'];
  },
  search: function (customerId: string, query: unknown) {
    return [...this.base(customerId), 'search', query];
  },
  filtered: function (customerId: string, listState: unknown) {
    return [...this.base(customerId), 'filtered', listState];
  },
});

export const containerOrder = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'container-orders'],
  byCustomer: function (customerId: string) {
    return [...this.base(), 'by-customer', customerId];
  },
  detail: function (id: string) {
    return [...this.base(), 'detail', id];
  },
  searchResults: function (customerId: string, query: unknown) {
    return [...this.base(customerId), 'search-results', query];
  },
});
